import React, { useEffect, useRef, useState } from 'react';
import registerServiceWorker from '../registerServiceWorker';
import { getBuildSignature, isNewBuild } from './build-update';

interface ServiceWorkerProviderState {
    updateAvailable: boolean,
    update: () => void
}

export const ServiceWorkerContext = React.createContext<ServiceWorkerProviderState | null>(null);

export const ServiceWorkerConsumer = ({ children }) => {
    return <ServiceWorkerContext.Consumer>
    {(serviceWorker: ServiceWorkerProviderState | null) => {
        if (!serviceWorker) return null;
        return children(serviceWorker)
    }}
    </ServiceWorkerContext.Consumer>
}

export default function ServiceWorkerProvider({ children }) {
    const [ swState, setSWState ] = useState<ServiceWorkerProviderState | null>(null);

    // Track a waiting service worker if present
    const waitingWorkerRef = useRef<ServiceWorker | null>(null);
    // Use this fetched baseline only when legacy metadata cannot be compared
    // with the identity embedded in the running bundle.
    const versionSigRef = useRef<string | null>(null);
    const visibilityRef = useRef<boolean>(document.visibilityState !== 'hidden');

    // These constants identify the loaded code, not the most recently deployed
    // metadata. They must remain fixed for this page's lifetime.
    const runningVersion = (process.env.REACT_APP_VERSION || '').toString();
    const runningBuildID = (process.env.REACT_APP_BUILD_ID || '').toString();

    // Unified method to expose for updating
    const triggerUpdate = () => {
        // Prefer service worker-based update if available
        if (waitingWorkerRef.current) {
            // Reload after the new SW takes control
            let didReload = false;
            const onControllerChange = () => {
                if (!didReload) {
                    didReload = true;
                    window.location.reload();
                }
            };
            navigator.serviceWorker && navigator.serviceWorker.addEventListener && navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true } as any);
            try {
                waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
            } catch (e) {
                // Fallback if posting fails
                window.location.reload();
            }
            // Safety fallback in case controllerchange doesn't fire
            setTimeout(() => {
                if (!didReload) window.location.reload();
            }, 1500);
        } else {
            // No SW path available, do a hard reload
            window.location.reload();
        }
    };

    // Service-worker and metadata detection feed the same update UI. Detection
    // only reports availability; the modal owns snoozing and user confirmation.
    const setUpdateAvailable = (available: boolean) => {
        setSWState({
            updateAvailable: available,
            update: triggerUpdate
        });
    };

    useEffect(() => {
        // Register Service Worker and listen for updates
        registerServiceWorker((newSW: ServiceWorker) => {
            // Capture a reference if there's a waiting/installed SW
            try {
                // newSW.state can be: installing, installed, activating, activated, redundant
                if (newSW && (newSW as any).state === 'installed' && navigator.serviceWorker.controller) {
                    waitingWorkerRef.current = newSW;
                    setUpdateAvailable(true);
                }
            } catch (e) {
                // no-op
            }
        });

        // Metadata polling also supports browsers without a working service
        // worker. Neither detection path applies the update automatically.
        let intervalId: any = null;
        let stopped = false;

        const fetchMeta = async () => {
            try {
                // Revalidate across browser/CDN caches so an old metadata response
                // does not hide a deployment. Server cache headers still apply.
                const res = await fetch(`${process.env.PUBLIC_URL || ''}/build-metadata.json?noCache=${Date.now()}`, {
                    cache: 'no-store'
                });
                if (!res.ok) return;
                const json = await res.json();
                // A request may finish after provider cleanup; do not publish its
                // result into a component that has already unmounted.
                if (stopped) return;
                const sig = getBuildSignature(json);
                if (!sig) return;
                if (isNewBuild(json, runningBuildID, runningVersion, versionSigRef.current)) {
                    setUpdateAvailable(true);
                } else if (versionSigRef.current === null) {
                    // Do not replace the baseline on later polls: it represents
                    // the first observed legacy deployment, not the latest one.
                    versionSigRef.current = sig;
                }
            } catch (e) {
                // Failed requests or invalid JSON do not prove a newer build
                // exists. Leave the current UI usable and retry on the next check.
            }
        };

        const visibleCheck = async () => {
            if (!visibilityRef.current) return;
            await fetchMeta();
        };

        // Initial fetch
        fetchMeta();

        // Periodic check when tab is visible
        intervalId = setInterval(() => {
            if (!stopped) visibleCheck();
        }, 60000);

        const onVisibilityChange = () => {
            visibilityRef.current = document.visibilityState !== 'hidden';
            if (visibilityRef.current) fetchMeta();
        };
        const onFocus = () => {
            visibilityRef.current = true;
            fetchMeta();
        };
        const onOnline = () => fetchMeta();

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
        window.addEventListener('online', onOnline);

        return () => {
            stopped = true;
            if (intervalId) clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
        };
    }, []);

    return <ServiceWorkerContext.Provider value={swState}>
        {children}
    </ServiceWorkerContext.Provider>
}
